/**
 * User Search Service
 *
 * Allows searching for other users by:
 * - Email
 * - Phone number
 * - Name
 *
 * Only returns users with public profiles
 */

import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  limit
} from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Search for users by email
 * @param {string} email
 * @returns {Promise<User[]>}
 */
export async function searchUsersByEmail(email) {
  try {
    if (!email || email.length < 2) return [];

    const q = query(
      collection(db, 'users'),
      where('email', '==', email.toLowerCase()),
      limit(10)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs
      .filter(doc => {
        const user = doc.data();
        // Default to public if profilePublic not set
        return user.profilePublic !== false;
      })
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
  } catch (error) {
    console.error('Error searching by email:', error);
    return [];
  }
}

/**
 * Search for users by phone number
 * @param {string} phone
 * @returns {Promise<User[]>}
 */
export async function searchUsersByPhone(phone) {
  try {
    if (!phone || phone.length < 3) return [];

    // Normalize phone (remove special chars)
    const normalized = phone.replace(/\D/g, '');

    const q = query(
      collection(db, 'users'),
      where('phone', '==', normalized),
      limit(10)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs
      .filter(doc => {
        const user = doc.data();
        // Default to public if profilePublic not set
        return user.profilePublic !== false;
      })
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
  } catch (error) {
    console.error('Error searching by phone:', error);
    return [];
  }
}

/**
 * Search for users by name (firstName, lastName, or displayName)
 * Uses client-side filtering as Firestore doesn't support OR queries well
 * Only returns users with public profiles
 * @param {string} name
 * @returns {Promise<User[]>}
 */
export async function searchUsersByName(name) {
  try {
    if (!name || name.length < 2) return [];

    const lowerName = name.toLowerCase();

    // Get all users - filter by privacy client-side
    const q = query(
      collection(db, 'users'),
      limit(500) // Increased limit to get more users
    );

    const snapshot = await getDocs(q);
    const results = snapshot.docs
      .filter(doc => {
        const user = doc.data();
        // Default to public if profilePublic not set
        return user.profilePublic !== false;
      })
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

    // Filter client-side: search firstName, lastName, or displayName
    return results
      .filter(user => {
        const firstName = user.firstName?.toLowerCase() || '';
        const lastName = user.lastName?.toLowerCase() || '';
        const displayName = user.displayName?.toLowerCase() || '';
        const email = user.email?.toLowerCase() || '';

        return (
          firstName.includes(lowerName) ||
          lastName.includes(lowerName) ||
          displayName.includes(lowerName) ||
          email.includes(lowerName) ||
          `${firstName} ${lastName}`.includes(lowerName)
        );
      })
      .slice(0, 10);
  } catch (error) {
    console.error('Error searching by name:', error);
    return [];
  }
}

/**
 * Universal search - tries email first, then phone, then name
 * @param {string} query
 * @returns {Promise<User[]>}
 */
export async function searchUsers(searchQuery) {
  try {
    if (!searchQuery || searchQuery.trim().length < 2) return [];

    const trimmed = searchQuery.trim();
    const allResults = [];

    console.log('🔍 Searching for:', trimmed);

    // Check if looks like email
    if (trimmed.includes('@')) {
      console.log('📧 Attempting email search...');
      const emailResults = await searchUsersByEmail(trimmed);
      console.log('✅ Email search results:', emailResults.length);
      allResults.push(...emailResults);
    }
    // Check if looks like phone (mostly digits)
    else if (/\d/.test(trimmed) && trimmed.length >= 3) {
      console.log('📞 Attempting phone search...');
      const phoneResults = await searchUsersByPhone(trimmed);
      console.log('✅ Phone search results:', phoneResults.length);
      allResults.push(...phoneResults);
    }

    // Always try name search
    console.log('🔤 Attempting name search...');
    const nameResults = await searchUsersByName(trimmed);
    console.log('✅ Name search results:', nameResults.length);
    allResults.push(...nameResults);

    // Deduplicate by user ID
    const uniqueResults = Array.from(
      new Map(allResults.map(user => [user.id, user])).values()
    );

    console.log('📊 Total unique results:', uniqueResults.length);
    console.log('Users found:', uniqueResults.map(u => ({ id: u.id, name: u.displayName, email: u.email })));

    return uniqueResults.slice(0, 15);
  } catch (error) {
    console.error('❌ Error searching users:', error);
    return [];
  }
}

/**
 * Get user profile by ID (public data only)
 * @param {string} userId
 * @returns {Promise<User|null>}
 */
export async function getUserPublicProfile(userId) {
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) return null;

    const userData = userSnap.data();
    // Default to public if profilePublic not set
    if (userData.profilePublic === false) return null;

    return {
      id: userSnap.id,
      ...userData
    };
  } catch (error) {
    console.error('Error getting user profile:', error);
    return null;
  }
}
